import log from "loglevel";
import { ChangeEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import throttle from "lodash.throttle";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@twilio-paste/core/box";
import { InputBox } from "@twilio-paste/core/input-box";
import { TextArea } from "@twilio-paste/core/textarea";
import { Button } from "@twilio-paste/core/button";
import { SendIcon } from "@twilio-paste/icons/esm/SendIcon";

import { AppState } from "../store/definitions";
import { AttachFileButton } from "./AttachFileButton";
import { FilePreview } from "./FilePreview";
import { detachFiles, updateAwaitingBot, updateWelcomeInputLock } from "../store/actions/genericActions";
import { isHumanMode } from "../store/selectors";
import { CHAR_LIMIT, WELCOME_INPUT_LOCK_MS } from "../constants";
import {
    formStyles,
    innerInputStyles,
    messageOptionContainerStyles,
    filePreviewContainerStyles,
    textAreaContainerStyles,
    inputRowStyles,
    attachButtonContainerStyles,
    inputBoxWrapperStyles
} from "./styles/MessageInput.styles";

export const MessageInput = () => {
    const dispatch = useDispatch();
    const [text, setText] = useState("");
    const [isSending, setIsSending] = useState(false);
    const { conversation, attachedFiles, fileAttachmentConfig, awaitingBot, welcomeInputLock, humanMode } = useSelector(
        (state: AppState) => ({
            conversation: state.chat.conversation,
            attachedFiles: state.chat.attachedFiles || [],
            fileAttachmentConfig: state.config.fileAttachment,
            awaitingBot: state.chat.awaitingBot,
            welcomeInputLock: state.chat.welcomeInputLock,
            humanMode: isHumanMode(state.chat)
        })
    );
    const oldAttachmentsLength = useRef((attachedFiles || []).length);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const attachmentsBoxRef = useRef<HTMLDivElement>(null);

    const throttleChange = useMemo(
        () =>
            throttle(() => {
                conversation?.typing();

                // in case the input was already focused, let's make sure to send the `read` status if the customer is typing
                if (conversation?.lastReadMessageIndex !== conversation?.lastMessage?.index) {
                    conversation?.setAllMessagesRead();
                }
            }, 500),
        [conversation]
    );

    /*
     * Trava fixa do composer ao iniciar a conversa (cobre o load do fluxo de boas-vindas, com
     * botões ou não — timer fixo, não orientado a evento). Libera após WELCOME_INPUT_LOCK_MS;
     * o cleanup limpa o timer se a janela fechar antes (re-agenda no próximo mount, sempre destrava).
     */
    useEffect(() => {
        if (!welcomeInputLock) {
            return undefined;
        }
        const timer = setTimeout(() => dispatch(updateWelcomeInputLock(false)), WELCOME_INPUT_LOCK_MS);
        return () => clearTimeout(timer);
    }, [welcomeInputLock, dispatch]);

    /*
     * Na fase IA, trava o envio enquanto espera o bot (anti-spam) e nos ~3s iniciais (welcomeInputLock).
     * No modo humano NUNCA trava (o cliente tem que escrever pro atendente a qualquer momento).
     */
    const isLocked = isSending || ((Boolean(awaitingBot) || Boolean(welcomeInputLock)) && !humanMode);
    const isSubmitDisabled = (!text.trim() && !attachedFiles?.length) || isLocked;
    // O botão de enviar só aparece quando há texto ou anexo
    const hasContent = Boolean(text.trim()) || Boolean(attachedFiles?.length);

    const send = async () => {
        if (isSubmitDisabled) {
            return;
        }
        if (!conversation) {
            log.error("Failed sending message: no conversation found");
            return;
        }
        setIsSending(true);
        /*
         * Só texto gera resposta da IA (o backend ignora mídia) e só na fase IA. Então:
         * - texto na fase IA → entra em "esperando o bot" na hora (balão + lock);
         * - só arquivo, OU modo humano → NÃO trava; garante o estado solto.
         */
        if (text.trim() && !humanMode) {
            dispatch(updateAwaitingBot(true));
        } else {
            dispatch(updateAwaitingBot(false));
        }

        let preparedMessage = conversation.prepareMessage();
        preparedMessage = preparedMessage.setBody(text);
        attachedFiles.forEach((file: File) => {
            const formData = new FormData();
            formData.append(file.name, file);
            preparedMessage.addMedia(formData);
        });
        await preparedMessage.build().send();
        setText("");
        dispatch(detachFiles(attachedFiles));
        setIsSending(false);
        textAreaRef.current?.focus();
    };

    const onKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            (e.target as HTMLInputElement).form?.dispatchEvent(
                new Event("submit", { cancelable: true, bubbles: true })
            );
        }
    };

    const onChange = (val: ChangeEvent<HTMLTextAreaElement>) => {
        setText(val.target.value);

        throttleChange();
    };

    const onFocus = () => {
        conversation?.setAllMessagesRead();
    };

    useEffect(() => {
        textAreaRef.current?.setAttribute("rows", "1");
        textAreaRef.current?.focus();
    }, [textAreaRef]);

    // Ensuring attached files are automatically scrolled to
    useEffect(() => {
        if (!attachmentsBoxRef.current) {
            return;
        }

        if (attachedFiles.length > oldAttachmentsLength.current) {
            (attachmentsBoxRef.current.lastChild as Element)?.scrollIntoView();
        }

        oldAttachmentsLength.current = attachedFiles.length;
    }, [attachedFiles]);

    return (
        <Box
            as="form"
            {...formStyles}
            onSubmit={(e) => {
                e.preventDefault();
                send();
            }}
        >
            {attachedFiles && attachedFiles.length > 0 && (
                <Box data-test="message-attachments" {...filePreviewContainerStyles} ref={attachmentsBoxRef}>
                    {attachedFiles.map((file, index) => (
                        <FilePreview focusable={true} key={index} file={file} isBubble={false} disabled={isSending} />
                    ))}
                </Box>
            )}
            <Box {...inputRowStyles}>
                {fileAttachmentConfig?.enabled && (
                    <Box {...attachButtonContainerStyles}>
                        <AttachFileButton textAreaRef={textAreaRef} />
                    </Box>
                )}
                <Box {...inputBoxWrapperStyles}>
                    <InputBox element="MESSAGE_INPUT_BOX" disabled={isLocked}>
                        <Box as="div" {...innerInputStyles}>
                            <Box {...textAreaContainerStyles}>
                                <TextArea
                                    ref={textAreaRef}
                                    data-test="message-input-textarea"
                                    placeholder="Escreva sua mensagem..."
                                    value={text}
                                    element="MESSAGE_INPUT"
                                    onChange={onChange}
                                    onFocus={onFocus}
                                    readOnly={isLocked}
                                    onKeyPress={onKeyPress}
                                    maxLength={CHAR_LIMIT}
                                />
                            </Box>
                            <Box {...messageOptionContainerStyles} style={{ display: hasContent ? "flex" : "none" }}>
                                <Button
                                    data-test="message-send-button"
                                    variant="primary_icon"
                                    size="icon_small"
                                    type="submit"
                                    aria-disabled={isSubmitDisabled}
                                >
                                    <SendIcon decorative={false} title="Enviar mensagem" size="sizeIcon30" />
                                </Button>
                            </Box>
                        </Box>
                    </InputBox>
                </Box>
            </Box>
        </Box>
    );
};
